import { tapResponse } from "@ngrx/operators";
import { DeepSignal, patchState, WritableStateSource } from "@ngrx/signals";
import { rxMethod, RxMethod } from "@ngrx/signals/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { PhDialog } from "@ph-core/services/dialog";
import { InsuranceResponseModel } from "@ph-model/api/response/insurance.response.model";
import { CheckoutFormValues } from "@ph-model/checkout/checkout-form-values.model";
import { PhNotificationComponent } from "@ph-shared/components/notification/notification/notification.component";
import { PhNotificationType } from "@ph-shared/components/notification/notification/notification.interface";
import { RequestQuotePopupComponent } from "@ph-shared/components/requested-quote-popup/requested-quote-popup.component";
import { Coverage, InsRequest } from "@ph-shopping/model/cart/request-quote";
import { VehicleStoreType } from "@ph-shopping/store/vehicle/vehicle.store";
import { map, pipe, switchMap, tap } from "rxjs";
import { CartApiClient } from "../cart-api.client";
import { NotificationService } from "@ph-core/services/notification";
import { convertToByteArray, updateCheckoutForm as patchCheckoutForm } from "../cart.helpers";

export interface CartStoreUMUQuoteForMethods extends WritableStateSource<object> {
    checkoutFormValues: DeepSignal<CheckoutFormValues>;

}

export interface CartStoreUMUQuoteDeps {
    cartApiClient: CartApiClient;
    vehicleStore: VehicleStoreType;
    dialog: PhDialog;
    notificationService: NotificationService;
}

export interface CartStoreUMUMethodsReturn {
    saveUMUQuote: RxMethod<InsRequest>;
    saveAsUMUQuoteSuccess: RxMethod<{ insResponse: InsuranceResponseModel }>;
    saveUMUQuoteFailure: RxMethod<string>;
    saveUMUCredit: RxMethod<{ request: InsRequest; locale: string }>;
}

export function withCartUMUMethods(
    store: CartStoreUMUQuoteForMethods,
    deps: CartStoreUMUQuoteDeps
): CartStoreUMUMethodsReturn {
    const { cartApiClient, vehicleStore, dialog, notificationService } = deps;

    const saveUMUQuote = rxMethod<InsRequest>(
        pipe(
            switchMap((insRequest: InsRequest) => {
                return cartApiClient.getRequestQuote(insRequest).pipe(
                    tapResponse<{ insResponse: InsuranceResponseModel }>({
                        next: (requestedQuote: { insResponse: InsuranceResponseModel }) => {
                            if (
                                requestedQuote.insResponse.generalInfo.covErrors &&
                                requestedQuote.insResponse.generalInfo.covErrors.length > 0
                            ) {
                                patchState(store, {
                                    error: requestedQuote.insResponse.generalInfo.covErrors[0].covError,
                                    isInValidRequestQuote: true,
                                });
                            }

                            if (requestedQuote.insResponse.insurance.coverage === null) {
                                patchState(store, {
                                    error: requestedQuote.insResponse.generalInfo.applicationResponse,
                                    isInValidRequestQuote: true,
                                });
                            }

                            patchState(store, { requestedQuote, isInValidRequestQuote: false });
                            saveAsUMUQuoteSuccess(requestedQuote);
                        },
                        error: (error) => {
                            console.error(error);
                            const errorMessage =
                                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                                    ? error.error
                                    : String(error)) || '';
                            patchState(store, { error: errorMessage, isInValidRequestQuote: true });
                            saveUMUQuoteFailure(errorMessage);
                        },
                    })
                );
            })
        )
    );

    const saveAsUMUQuoteSuccess = rxMethod<{ insResponse: InsuranceResponseModel }>(
        pipe(
            tap(() => dialog.open(RequestQuotePopupComponent)),
            map(({ insResponse }: { insResponse: InsuranceResponseModel }) => insResponse.insurance.coverage),
            tap((umuCoverages: Coverage[]) => {
                const insuranceValues = store.checkoutFormValues.creditInsuranceForm();
                const updatedLoanAmount = umuCoverages.reduce(
                    (total, coverage) => total + Number(coverage.premium) + Number(coverage.tax),
                    Number(insuranceValues.loanAmount)
                );

                patchCheckoutForm(
                    {
                        getCheckoutFormValues: () => store.checkoutFormValues(),
                        patchCheckoutFormValues: (values) => patchState(store, { checkoutFormValues: values }),
                        vehicleStore,
                    },
                    {
                        creditInsuranceForm: { ...insuranceValues, loanAmount: +updatedLoanAmount.toFixed(2) },
                    }
                );
            })
        )
    );

    const saveUMUQuoteFailure = rxMethod<string>(
        pipe(
            switchMap((error: string) =>
                dialog
                    .open(PhNotificationComponent, {
                        data: {
                            type: PhNotificationType.cancel,
                            actions: [{ text: 'Close', act: (ref) => ref.close() }],
                            value: {
                                title: 'Quote saving failed',
                                message: error,
                            },
                        },
                    })
                    .afterClosed()
            )
        )
    );

    const saveUMUCredit = rxMethod<{ request: InsRequest; locale: string }>(
        pipe(
            switchMap(({ request, locale }: { request: InsRequest; locale: string }) => {
                return cartApiClient.getUMUCredit(request, locale).pipe(
                    tapResponse<{
                        insResponse: { responseStatus: string; applicationResponse: string };
                    }>({
                        next: (response) => {
                            if (response.insResponse.responseStatus === '1' || response.insResponse.responseStatus === '') {
                                const error = response.insResponse.applicationResponse;
                                notificationService.push({
                                    type: PhNotificationType.cancel,
                                    value: { title: 'Warning!', message: error },
                                });
                                patchState(store, { error, isValidSaveCredit: false });
                            }

                            const pdf = response.insResponse.applicationResponse;

                            if (pdf) {
                                const blob: string = atob(pdf);
                                const file = new Blob(convertToByteArray(blob), { type: 'application/pdf' });
                                const fileURL = URL.createObjectURL(file);
                                window.open(fileURL, '_blank');
                            }

                            notificationService.push({
                                type: PhNotificationType.approved,
                                value: {
                                    title: 'Success!',
                                    message: 'Insurance contract was successfully created!',
                                },
                                duration: 4000,
                            });

                            patchState(store, { isValidSaveCredit: true });
                        },
                        error: (error: unknown) => {
                            console.error(error);
                            const errorMessage =
                                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                                    ? error.error
                                    : String(error)) || '';
                            notificationService.push({
                                type: PhNotificationType.cancel,
                                value: { title: 'Warning!', message: errorMessage },
                            });

                            patchState(store, { error: errorMessage, isValidSaveCredit: false });
                        },
                    })
                );
            })
        )
    );

    return { saveUMUQuote, saveAsUMUQuoteSuccess, saveUMUQuoteFailure, saveUMUCredit }
}
